<template>
  <settings-modal
    class="forex-currency-pair-editor-modal t-forex-currency-pair-editor-modal"
  >
    <span
      slot="heading"
      class="font-weight-bold"
    >{{ title }}</span>

    <data-row
      v-if="modalData.currencyPair"
      style="margin-bottom: 17px;"
    >
      <v-flex>
        <one-data-column
          :label-props="{ style: 'width: 150px;' }"
          :data-props="{ style: 'min-width: auto;', class: 'font-weight-medium' }"
        >
          <data-label slot="label">Currency pair</data-label>
          <forex-currency-pair-currencies
            :from="modalData.currencyPair.fromCurrency"
            :to="modalData.currencyPair.toCurrency"
          />
        </one-data-column>
      </v-flex>
    </data-row>

    <v-layout
      v-else
      row
    >
      <v-flex xs6>
        <v-autocomplete
          v-model="form.fromCurrency"
          class="pt-0"
          label="First currency"
          :items="fromCurrencyOptions"
          :error-messages="getValidationErrorMessage('fromCurrency')"
          @change="onFromCurrencyChange"
        />
      </v-flex>

      <v-flex xs6>
        <v-autocomplete
          v-model="form.toCurrency"
          class="pt-0"
          label="Second currency"
          :items="toCurrencyOptions"
          :error-messages="getValidationErrorMessage('toCurrency')"
        />
      </v-flex>
    </v-layout>

    <v-layout row>
      <v-flex xs6>
        <div>
          <div class="v-label theme--light mb-2">{{ autoRatesTitle }}</div>
          <v-switch
            v-model="form.useAutoRate"
            class="pt-0 mt-0"
            color="primary"
            :label="form.useAutoRate ? 'Enabled' : 'Disabled'"
            :disabled="!hasAlternativeRate"
            hide-details
            @change="onAutoRateToggle"
          />
        </div>
      </v-flex>
    </v-layout>

    <v-layout row>
      <v-flex xs6>
        <v-text-field
          v-model="form.buyRate"
          class="pt-1"
          type="number"
          :label="form.fromCurrency != null ? `Buy ${form.fromCurrency}` : 'Buy'"
          :error-messages="getValidationErrorMessage('buyRate')"
          :disabled="form.useAutoRate"
        />
        <v-switch
          v-model="form.buyEnabled"
          class="pt-0 mt-0"
          color="primary"
          :label="form.buyEnabled ? 'Enable buy' : 'Disable buy'"
          hide-details
        />
      </v-flex>

      <v-flex
        xs6
      >
        <v-text-field
          v-model="form.sellRate"
          class="pt-1"
          type="number"
          :label="form.toCurrency != null ? `Sell ${form.fromCurrency}` : 'Sell'"
          :error-messages="getValidationErrorMessage('sellRate')"
          :disabled="form.useAutoRate"
        />
        <v-switch
          v-model="form.sellEnabled"
          class="pt-0 mt-0"
          color="primary"
          :label="form.sellEnabled ? 'Enable sell' : 'Disable sell'"
          hide-details
        />
      </v-flex>
    </v-layout>

    <data-heading
      heading="Period of activity"
      spaced
    />

    <v-layout row>
      <v-flex xs6>
        <v-checkbox
          v-model="form.buyDayMarginEnabled"
          :ripple="false"
          label="Day buy"
          color="primary"
          hide-details
          @change="onPeriodMarginCheckboxChange($event, 'buyDayMargin')"
        />
        <v-text-field
          v-model="form.buyDayMargin"
          class="forex-currency-pair-add-modal__margin-field"
          type="number"
          label="Margin"
          suffix="%"
          :disabled="!form.buyDayMarginEnabled"
          :error-messages="getValidationErrorMessage('buyDayMargin')"
        />
      </v-flex>

      <v-flex xs6>
        <v-checkbox
          v-model="form.sellDayMarginEnabled"
          :ripple="false"
          label="Day sell"
          color="primary"
          hide-details
          @change="onPeriodMarginCheckboxChange($event, 'sellDayMargin')"
        />
        <v-text-field
          v-model="form.sellDayMargin"
          class="forex-currency-pair-add-modal__margin-field"
          type="number"
          label="Margin"
          suffix="%"
          :disabled="!form.sellDayMarginEnabled"
          :error-messages="getValidationErrorMessage('sellDayMargin')"
        />
      </v-flex>
    </v-layout>

    <v-layout row>
      <v-flex xs6>
        <v-checkbox
          v-model="form.buyNightMarginEnabled"
          :ripple="false"
          label="Night buy"
          color="primary"
          hide-details
          @change="onPeriodMarginCheckboxChange($event, 'buyNightMargin')"
        />
        <v-text-field
          v-model="form.buyNightMargin"
          class="forex-currency-pair-add-modal__margin-field"
          type="number"
          label="Margin"
          suffix="%"
          :disabled="!form.buyNightMarginEnabled"
          :error-messages="getValidationErrorMessage('buyNightMargin')"
        />
      </v-flex>

      <v-flex xs6>
        <v-checkbox
          v-model="form.sellNightMarginEnabled"
          :ripple="false"
          label="Night sell"
          color="primary"
          hide-details
          @change="onPeriodMarginCheckboxChange($event, 'sellNightMargin')"
        />
        <v-text-field
          v-model="form.sellNightMargin"
          class="forex-currency-pair-add-modal__margin-field"
          type="number"
          label="Margin"
          suffix="%"
          :disabled="!form.sellNightMarginEnabled"
          :error-messages="getValidationErrorMessage('sellNightMargin')"
        />
      </v-flex>
    </v-layout>

    <v-layout row>
      <v-flex xs6>
        <v-checkbox
          v-model="form.buyWeekendMarginEnabled"
          :ripple="false"
          label="Weekends buy"
          color="primary"
          hide-details
          @change="onPeriodMarginCheckboxChange($event, 'buyWeekendMargin')"
        />
        <v-text-field
          v-model="form.buyWeekendMargin"
          class="forex-currency-pair-add-modal__margin-field"
          type="number"
          label="Margin"
          suffix="%"
          :disabled="!form.buyWeekendMarginEnabled"
          :error-messages="getValidationErrorMessage('buyWeekendMargin')"
        />
      </v-flex>

      <v-flex xs6>
        <v-checkbox
          v-model="form.sellWeekendMarginEnabled"
          :ripple="false"
          label="Weekends sell"
          color="primary"
          hide-details
          @change="onPeriodMarginCheckboxChange($event, 'sellWeekendMargin')"
        />
        <v-text-field
          v-model="form.sellWeekendMargin"
          class="forex-currency-pair-add-modal__margin-field"
          type="number"
          label="Margin"
          suffix="%"
          :disabled="!form.sellWeekendMarginEnabled"
          :error-messages="getValidationErrorMessage('sellWeekendMargin')"
        />
      </v-flex>
    </v-layout>

    <v-layout row>
      <v-flex xs6>
        <div class="forex-currency-pair-add-modal__fee">
          <div class="v-label theme--light mb-2">Fees buy</div>
          <v-switch
            v-model="form.buyFee"
            class="pt-0 mt-0"
            color="primary"
            :label="form.buyFee ? 'Enabled' : 'Disabled'"
            hide-details
          />
        </div>
      </v-flex>

      <v-flex xs6>
        <div class="forex-currency-pair-add-modal__fee">
          <div class="v-label theme--light mb-2">Fees sell</div>
          <v-switch
            v-model="form.sellFee"
            class="pt-0 mt-0"
            color="primary"
            :label="form.sellFee ? 'Enabled' : 'Disabled'"
            hide-details
          />
        </div>
      </v-flex>
    </v-layout>

    <error-alert :error="error" />

    <template slot="actions">
      <v-btn
        class="t-confirm-button"
        color="primary"
        medium
        flat
        :disabled="!isFormFilled || loading"
        @click="onConfirmButtonClick"
      >{{ confirmButtonText }}</v-btn>

      <v-btn
        class="t-cancel-button"
        color="primary"
        medium
        flat
        @click="onCloseButtonClick"
      >cancel</v-btn>

      <template v-if="modalData.currencyPair != null">
        <v-spacer />

        <v-btn
          class="t-delete-button"
          color="error"
          medium
          flat
          @click="onDeleteButtonClick"
        >delete pair</v-btn>
      </template>
    </template>
  </settings-modal>
</template>

<script>
  // Utils
  import { currencyPairFormDataToRequestData, currencyPairToFormData } from '@/utils/Forex'

  // Data
  import currencies from '@/assets/data/currencies'
  import cryptoCurrencies from '@/assets/data/crypto-currencies'

  // Events
  import { EVENT_FOREX_CURRENCY_PAIR_CHANGE } from '@/events/forexCurrencyPairs'

  // Services
  import ApiRequest from '@/services/ApiRequests/ApiRequest'
  import ForexCurrencyRatesApi from '@/services/Api/ForexCurrencyRatesApi'

  // Mixins
  import form from '@/mixins/form'
  import modal from '@/mixins/modal'
  import flashMessages from '@/mixins/flashMessages'

  // Components
  import SettingsModal from '@/components/modals/SettingsModal'
  import ErrorAlert from '@/components/ui/ErrorAlert'
  import DataHeading from '@/components/ui/Data/DataHeading'
  import DataRow from '../ui/Data/DataRow'
  import DataLabel from '@/components/ui/Data/DataLabel'
  import OneDataColumn from '@/components/ui/Data/OneDataColumn'
  import ForexCurrencyPairCurrencies from '@/components/forex/ForexCurrencyPairCurrencies'

  export default {
    name: 'ForexCurrencyPairEditorModal',
    components: {
      SettingsModal,
      ErrorAlert,
      DataHeading,
      DataRow,
      DataLabel,
      OneDataColumn,
      ForexCurrencyPairCurrencies
    },
    mixins: [form, modal, flashMessages],
    data () {
      return {
        currencies: [...currencies, ...cryptoCurrencies],
        form: {
          fromCurrency: null,
          toCurrency: null,
          buyRate: null,
          buyDayMargin: null,
          buyDayMarginEnabled: false,
          buyNightMargin: null,
          buyNightMarginEnabled: false,
          buyWeekendMargin: null,
          buyWeekendMarginEnabled: false,
          buyFee: false,
          buyEnabled: true,
          sellRate: null,
          sellDayMargin: null,
          sellDayMarginEnabled: false,
          sellNightMargin: null,
          sellNightMarginEnabled: false,
          sellWeekendMargin: null,
          sellWeekendMarginEnabled: false,
          sellFee: false,
          sellEnabled: true,
          useAutoRate: false
        },
        saveCurrencyPairApiRequest: new ApiRequest(ForexCurrencyRatesApi.savePair)
      }
    },
    computed: {
      title () {
        return `${this.currencyPair != null ? 'Edit' : 'Add'} currency pair`
      },
      currencyPair () {
        return this.modalData.currencyPair
      },
      hasAlternativeRate () {
        return this.hasCurrencyPair &&
          this.currencyPair.buy.alternativeRate != null &&
          this.currencyPair.sell.alternativeRate != null
      },
      autoRatesTitle () {
        return this.hasAlternativeRate ? 'Auto rate' : 'No auto rates available'
      },
      hasCurrencyPair () {
        return this.currencyPair != null
      },
      confirmButtonText () {
        return this.hasCurrencyPair ? 'save changes' : 'add currency pair'
      },
      requiredFields () {
        const requiredFields = [
          'fromCurrency',
          'toCurrency',
          'buyRate',
          'sellRate'
        ]

        if (this.form.buyDayMarginEnabled) {
          requiredFields.push('buyDayMargin')
        }

        if (this.form.buyNightMarginEnabled) {
          requiredFields.push('buyNightMargin')
        }

        if (this.form.buyWeekendMarginEnabled) {
          requiredFields.push('buyWeekendMargin')
        }

        if (this.form.sellDayMarginEnabled) {
          requiredFields.push('sellDayMargin')
        }

        if (this.form.sellNightMarginEnabled) {
          requiredFields.push('sellNightMargin')
        }

        if (this.form.sellWeekendMarginEnabled) {
          requiredFields.push('sellWeekendMargin')
        }

        return requiredFields
      },
      error () {
        return this.saveCurrencyPairApiRequest.error
      },
      fromCurrencyOptions () {
        return [...currencies, ...cryptoCurrencies]
      },
      toCurrencyOptions () {
        return [...currencies, ...cryptoCurrencies].filter((currency) => this.form.fromCurrency !== currency)
      },
      loading () {
        return this.saveCurrencyPairApiRequest.loading
      }
    },
    created () {
      if (this.currencyPair != null) {
        this.setFormData(currencyPairToFormData(this.currencyPair))
      }
    },
    methods: {
      onFromCurrencyChange (fromCurrency) {
        if (fromCurrency === this.form.toCurrency) {
          this.setFieldValue('toCurrency', null)
        }
      },
      onPeriodMarginCheckboxChange (checkboxValue, field) {
        if (checkboxValue === false) {
          this.setFieldValue(field, null)
        }
      },
      onAutoRateToggle (switchValue) {
        this.setFormData({
          buyRate: this.currencyPair.useAutoRate === switchValue ? this.currencyPair.buy.rate : this.currencyPair.buy.alternativeRate,
          sellRate: this.currencyPair.useAutoRate === switchValue ? this.currencyPair.sell.rate : this.currencyPair.sell.alternativeRate
        })
      },
      onConfirmButtonClick () {
        this.saveCurrencyPairApiRequest
          .execute(currencyPairFormDataToRequestData(this.parsedFormInput))
          .then(() => {
            this.$eventHub.$emit(EVENT_FOREX_CURRENCY_PAIR_CHANGE)

            this.showSuccessFlashMessage(
              this.currencyPair != null
                ? 'Currency pair has been successfully edited.'
                : 'Currency pair has been successfully added.'
            )
          })
          .catch(() => {})
      },
      onDeleteButtonClick () {
        const { currencyPair } = this.modalData

        setTimeout(() => {
          this.openModal(
            'ForexCurrencyPairDeleteModal',
            { currencyPair },
            { size: '500px' }
          )
        }, 100)

        this.closeModal()
      },
      onCloseButtonClick () {
        this.closeModal()
      }
    }
  }
</script>

<style lang="scss">
  .forex-currency-pair-editor-modal {
    &__fee {
      margin: 0 0 2px 32px;
    }

    &__margin-field {
      margin-left: 32px;
    }
  }
</style>
